import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Input, Spin, notification } from "antd";
import {
  WhatsAppOutlined,
  PhoneOutlined,
  SendOutlined,
  ThunderboltOutlined,
  SettingOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  fetchConversations,
  fetchMessages,
  sendWhatsappMessage,
} from "../WhatsappInbox/service";
import {
  fetchActivities,
  approveActivity,
  rejectActivity,
} from "./service";
import { STATUS_COLORS, formatRelative } from "./constants";
import styles from "./style.module.scss";
import { BrandTag } from "pages/SurveyManagement/phoneTriggerShared";

const POLL_MS = 10000;

const initials = (label) =>
  (label || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const maskCustomer = (item) => {
  const cref = item.customer_ref || {};
  if (cref.name) return cref.name;
  if (cref.phone) {
    const p = String(cref.phone);
    return p.length > 4 ? `···${p.slice(-4)}` : p;
  }
  if (cref.email) {
    const e = String(cref.email);
    const at = e.indexOf("@");
    return at > 1 ? `${e[0]}···${e.slice(at)}` : e;
  }
  return `Customer #${String(item.id || "0000").slice(-4)}`;
};

const statusDisplay = (status, t) => {
  const map = {
    sent: t("sonic.status_sent_short"),
    delivered: t("sonic.status_delivered_short"),
    replied: t("sonic.status_replied_short"),
    failed: t("sonic.status_failed_short"),
    pending_approval: t("sonic.status_pending_short"),
    scheduled: t("sonic.status_scheduled_short"),
    in_progress: t("sonic.status_in_progress_short"),
    completed: t("sonic.status_completed_short"),
  };
  return map[status] || status;
};

/** ``readOnly``: the Engage plan has ended; conversations are shown, no reply is sent. */
const ConversationsTab = ({ active, onOpenConfigure, onGoInsights, readOnly = false }) => {
  const { t } = useTranslation();
  const subscriptionStatus = useSelector((state) => state.userReducer);
  const whatsappConnected = subscriptionStatus?.whatsapp_status === "active";

  const [loadingThreads, setLoadingThreads] = useState(true);
  const [waConversations, setWaConversations] = useState([]);
  const [voiceActivities, setVoiceActivities] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);

  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [actioningId, setActioningId] = useState(null);
  const messagesEndRef = useRef(null);

  const selectedThread = useMemo(() => {
    if (!selectedKey) return null;
    if (selectedKey.startsWith("wa-")) {
      const id = Number(selectedKey.slice(3));
      const c = waConversations.find((x) => x.id === id);
      if (!c) return null;
      const label = c.customer_name || c.customer_wa_id;
      return {
        key: selectedKey,
        kind: "whatsapp",
        id: c.id,
        label,
        time: c.last_message_at,
        unread: c.unread_count || 0,
        preview: c.last_message_preview || "",
        data: c,
      };
    }
    if (selectedKey.startsWith("voice-")) {
      const id = selectedKey.slice(6);
      const a = voiceActivities.find((x) => String(x.id) === id);
      if (!a) return null;
      return {
        key: selectedKey,
        kind: "voice",
        id: a.id,
        label: maskCustomer(a),
        time: a.created_at,
        unread: 0,
        preview: a.reply_text || statusDisplay(a.status, t),
        data: a,
      };
    }
    return null;
  }, [selectedKey, waConversations, voiceActivities, t]);

  const threads = useMemo(() => {
    const wa = waConversations.map((c) => {
      const label = c.customer_name || c.customer_wa_id;
      return {
        key: `wa-${c.id}`,
        kind: "whatsapp",
        id: c.id,
        label,
        time: c.last_message_at,
        unread: c.unread_count || 0,
        preview: c.last_message_preview || "",
        data: c,
      };
    });
    const voice = voiceActivities.map((a) => ({
      key: `voice-${a.id}`,
      kind: "voice",
      id: a.id,
      label: maskCustomer(a),
      time: a.created_at,
      unread: 0,
      preview: a.reply_text || statusDisplay(a.status, t),
      data: a,
    }));
    return [...wa, ...voice].sort((a, b) => {
      const ta = new Date(a.time || 0).getTime();
      const tb = new Date(b.time || 0).getTime();
      return tb - ta;
    });
  }, [waConversations, voiceActivities, t]);

  const loadThreads = useCallback(async () => {
    const tasks = [
      fetchActivities({ channel: "voice_call", limit: 50 }),
    ];
    if (whatsappConnected) {
      tasks.unshift(fetchConversations());
    }
    const results = await Promise.all(tasks);
    let idx = 0;
    if (whatsappConnected) {
      const waRes = results[idx++];
      if (waRes && !waRes.error && Array.isArray(waRes.conversations)) {
        setWaConversations(waRes.conversations);
      } else if (waRes && waRes.error) {
        notification.error({ message: waRes.error });
      }
    } else {
      setWaConversations([]);
    }
    const voiceRes = results[idx];
    if (voiceRes && !voiceRes.error) {
      setVoiceActivities(voiceRes.activities || []);
    }
    setLoadingThreads(false);
  }, [whatsappConnected]);

  const loadMessages = useCallback(async (conversationId, { silent } = {}) => {
    if (!conversationId) return;
    if (!silent) setLoadingMessages(true);
    const res = await fetchMessages(conversationId);
    if (res && !res.error && Array.isArray(res.messages)) {
      setMessages(res.messages);
    }
    if (!silent) setLoadingMessages(false);
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    setLoadingThreads(true);
    loadThreads();
    return undefined;
  }, [active, loadThreads]);

  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(loadThreads, POLL_MS);
    return () => clearInterval(id);
  }, [active, loadThreads]);

  useEffect(() => {
    if (!active || !selectedThread || selectedThread.kind !== "whatsapp") {
      setMessages([]);
      return undefined;
    }
    loadMessages(selectedThread.id);
    return undefined;
  }, [active, selectedThread, loadMessages]);

  useEffect(() => {
    if (!active || !selectedThread || selectedThread.kind !== "whatsapp") return undefined;
    const id = setInterval(
      () => loadMessages(selectedThread.id, { silent: true }),
      POLL_MS
    );
    return () => clearInterval(id);
  }, [active, selectedThread, loadMessages]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const onSelectThread = (thread) => {
    setSelectedKey(thread.key);
    if (thread.kind === "whatsapp" && thread.data.unread_count) {
      setWaConversations((prev) =>
        prev.map((c) =>
          c.id === thread.id ? { ...c, unread_count: 0 } : c
        )
      );
    }
  };

  const onSend = async () => {
    const text = draft.trim();
    const conv = selectedThread?.kind === "whatsapp" ? selectedThread.data : null;
    if (!text || !conv || sending) return;
    setSending(true);
    const res = await sendWhatsappMessage({
      to: conv.customer_wa_id,
      message: text,
    });
    setSending(false);
    if (res && !res.error && res.message) {
      setDraft("");
      setMessages((prev) => [...prev, res.message]);
      loadThreads();
    } else {
      notification.error({
        message: (res && res.error) || t("whatsappInbox.send_failed"),
      });
    }
  };

  const onDraftKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const handleApprove = async (id) => {
    setActioningId(id);
    const res = await approveActivity(id);
    setActioningId(null);
    if (res && !res.error) {
      notification.success({ message: t("sonic.activity_approved") });
      loadThreads();
    } else {
      notification.error({ message: (res && res.error) || t("sonic.action_failed") });
    }
  };

  const handleReject = async (id) => {
    setActioningId(id);
    const res = await rejectActivity(id);
    setActioningId(null);
    if (res && !res.error) {
      notification.success({ message: t("sonic.activity_rejected") });
      loadThreads();
    } else {
      notification.error({ message: (res && res.error) || t("sonic.action_failed") });
    }
  };

  const insightContext = useMemo(() => {
    if (!selectedThread) return null;
    if (selectedThread.kind === "voice") {
      const a = selectedThread.data;
      return {
        ruleName: a.rule_name,
        trigger: a.trigger_source || t("sonic.manual_trigger"),
        reply: a.reply_text,
        status: a.status,
      };
    }
    return {
      ruleName: null,
      trigger: t("sonic.conversations_wa_thread"),
      reply: selectedThread.preview,
      status: null,
    };
  }, [selectedThread, t]);

  if (!active) return null;

  return (
    <div className={styles.tabInner}>
      <div className={styles.conversationsLayout}>
        <div className={styles.conversationsList}>
          <div className={styles.conversationsListHeader}>
            {t("sonic.conversations_title")}
          </div>
          {!whatsappConnected && !readOnly && (
            <div className={styles.conversationsBanner}>
              <span>{t("sonic.conversations_wa_not_connected")}</span>
              <Button
                type="link"
                size="small"
                icon={<SettingOutlined />}
                onClick={() => onOpenConfigure("organization")}
              >
                {t("sonic.configure_channels")}
              </Button>
            </div>
          )}
          {loadingThreads ? (
            <div className={styles.conversationsCentered}>
              <Spin />
            </div>
          ) : threads.length === 0 ? (
            <div className={styles.conversationsEmpty}>
              <ThunderboltOutlined className={styles.conversationsEmptyIcon} />
              <p>{t("sonic.conversations_empty")}</p>
            </div>
          ) : (
            threads.map((thread) => (
              <button
                type="button"
                key={thread.key}
                className={`${styles.conversationsItem} ${
                  selectedKey === thread.key ? styles.conversationsItemActive : ""
                }`}
                onClick={() => onSelectThread(thread)}
              >
                <div
                  className={`${styles.conversationsAvatar} ${
                    thread.kind === "voice" ? styles.conversationsAvatarVoice : ""
                  }`}
                >
                  {thread.kind === "voice" ? (
                    <PhoneOutlined />
                  ) : (
                    initials(thread.label)
                  )}
                </div>
                <div className={styles.conversationsMeta}>
                  <div className={styles.conversationsTopRow}>
                    <span className={styles.conversationsName}>
                      {thread.kind === "whatsapp" && (
                        <WhatsAppOutlined className={styles.conversationsChannelIcon} />
                      )}
                      {thread.label}
                    </span>
                    <span className={styles.conversationsTime}>
                      {formatRelative(thread.time) || formatTime(thread.time)}
                    </span>
                  </div>
                  <div className={styles.conversationsPreview}>{thread.preview}</div>
                </div>
                {thread.unread > 0 && (
                  <Badge count={thread.unread} className={styles.conversationsUnread} />
                )}
              </button>
            ))
          )}
        </div>

        <div className={styles.conversationsMain}>
          {!selectedThread ? (
            <div className={styles.conversationsMainEmpty}>
              <WhatsAppOutlined className={styles.conversationsMainEmptyIcon} />
              <p>{t("sonic.conversations_select")}</p>
            </div>
          ) : selectedThread.kind === "whatsapp" ? (
            <>
              <div className={styles.conversationsChatHeader}>
                <div className={styles.conversationsAvatar}>
                  {initials(selectedThread.label)}
                </div>
                <div>
                  <div className={styles.conversationsChatName}>
                    {selectedThread.label}
                  </div>
                  <div className={styles.conversationsChatSub}>
                    +{selectedThread.data.customer_wa_id}
                  </div>
                </div>
              </div>
              <div className={styles.conversationsMessages}>
                {loadingMessages ? (
                  <div className={styles.conversationsCentered}>
                    <Spin />
                  </div>
                ) : (
                  messages.map((m) => (
                    <div
                      key={m.id}
                      className={`${styles.conversationsMessageRow} ${
                        m.direction === "outbound"
                          ? styles.conversationsOutbound
                          : styles.conversationsInbound
                      }`}
                    >
                      <div className={styles.conversationsBubble}>
                        <span>{m.body}</span>
                        <span className={styles.conversationsBubbleTime}>
                          {formatTime(m.created_at)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>
              {readOnly ? null : (
              <div className={styles.conversationsComposer}>
                <Input.TextArea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyPress={onDraftKeyPress}
                  placeholder={t("whatsappInbox.message_placeholder")}
                  autoSize={{ minRows: 1, maxRows: 4 }}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={sending}
                  onClick={onSend}
                  disabled={!draft.trim()}
                >
                  {t("whatsappInbox.send")}
                </Button>
              </div>
              )}
            </>
          ) : (
            <div className={styles.conversationsCallDetail}>
              <div className={styles.conversationsCallHeader}>
                <PhoneOutlined className={styles.conversationsCallIcon} />
                <div>
                  <h3>{selectedThread.label}</h3>
                  <p>{formatRelative(selectedThread.data.created_at)}</p>
                </div>
                <BrandTag tone={STATUS_COLORS[selectedThread.data.status]}>
                  {statusDisplay(selectedThread.data.status, t)}
                </BrandTag>
              </div>
              <dl className={styles.conversationsCallMeta}>
                <dt>{t("sonic.col_rule")}</dt>
                <dd>{selectedThread.data.rule_name || t("sonic.manual_trigger")}</dd>
                <dt>{t("sonic.col_channel")}</dt>
                <dd>{t("sonic.channel_voice")}</dd>
                {selectedThread.data.reply_text && (
                  <>
                    <dt>{t("sonic.conversations_transcript")}</dt>
                    <dd>{selectedThread.data.reply_text}</dd>
                  </>
                )}
              </dl>
              {selectedThread.data.status === "pending_approval" && (
                <div className={styles.conversationsCallActions}>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={actioningId === selectedThread.id}
                    onClick={() => handleApprove(selectedThread.id)}
                  >
                    {t("sonic.approve")}
                  </Button>
                  <Button
                    danger
                    icon={<CloseOutlined />}
                    loading={actioningId === selectedThread.id}
                    onClick={() => handleReject(selectedThread.id)}
                  >
                    {t("sonic.reject")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        <aside className={styles.conversationsInsight}>
          <div className={styles.conversationsInsightPanel}>
            <h3>{t("sonic.conversations_insight_title")}</h3>
            {!insightContext ? (
              <p className={styles.conversationsInsightMuted}>
                {t("sonic.conversations_insight_empty")}
              </p>
            ) : (
              <>
                {insightContext.ruleName && (
                  <div className={styles.conversationsInsightBlock}>
                    <span className={styles.conversationsInsightLabel}>
                      {t("sonic.col_rule")}
                    </span>
                    <span>{insightContext.ruleName}</span>
                  </div>
                )}
                <div className={styles.conversationsInsightBlock}>
                  <span className={styles.conversationsInsightLabel}>
                    {t("sonic.conversations_trigger")}
                  </span>
                  <span>{insightContext.trigger}</span>
                </div>
                {insightContext.reply && (
                  <div className={styles.conversationsInsightBlock}>
                    <span className={styles.conversationsInsightLabel}>
                      {t("sonic.conversations_context")}
                    </span>
                    <p className={styles.conversationsInsightText}>
                      {insightContext.reply}
                    </p>
                  </div>
                )}
                <Button type="link" onClick={onGoInsights}>
                  {t("sonic.conversations_view_insights")}
                </Button>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ConversationsTab;
